
/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
*/
import useKeyCommand, {KeyCommandHandler} from './useKeyCommand';

/** 
 * The `useCloseKeyCommand` hook provides a callback function to close dialogs
 * when the ESC key is pressed. This function can be passed to components
 * that handle keyboard events, such as the `onKeyDown`, `onKeyUp`, etc.
 * 
 * Components can use this hook to provide a function that handles ESC key
 *
 * ```typescript
 *   import useCloseKeyCommand from '@dls/src/hooks/useCloseKeyCommand';
 *
 *   const SomeComponent = ({onClose}: SomeComponentProps) => {
 *     const onKeyCommand = useCloseKeyCommand(onClose);
 *     ...
 *     return (
 *       <Dialog open={open}
 *        // pass onKeyCommand function to Dialog's onKeyDown prop ...
 *        onKeyDown={onKeyCommand} ... />
 *     );
 *   };
 * ```
 * 
 * @param close The function to be invoked when the `Escape` key is pressed.
 * @returns A function which handles a `KeyboardEvent` that can be passed to a
 *          an applicable HTML DOM keyboard event attribute.
 */
export const useCloseKeyCommand = (close?: Function): KeyCommandHandler => (
  useKeyCommand('Escape', close)
);

export default useCloseKeyCommand;
